import * as React from 'react';
import { forwardRef, useState } from 'react';

import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import { Spacer } from '@repo/ui/components/Spacer';
import { H1 } from '@repo/ui/components/Typography';

import PreviewModal from 'components/BackgroundReplacer/BackgroundReplacerTool/PreviewModal';
import Gallery from 'components/BackgroundReplacer/Gallery/Gallery';
import { GeneratedImage } from 'components/BackgroundReplacer/Gallery/types';
import Pagination from 'components/BackgroundReplacer/Pagination/Pagination';
import { useBackgroundReplacerStore } from 'stores/backgroundReplacerStore/backgroundReplacerStore';
import { FlexColumn } from 'styles/layouts';

interface GallerySectionProps {
  onSelectPage: (page: number) => void;
  galleryTitle?: string;
}

const GallerySection = forwardRef<HTMLDivElement, GallerySectionProps>(
  ({ galleryTitle, onSelectPage }, ref) => {
    const isLoggedIn = useUserStore(userStoreSelector.isLoggedIn);

    const { state } = useBackgroundReplacerStore();
    const [openedElement, setOpenedElement] = useState<GeneratedImage | null>(
      null
    );
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    if (!isLoggedIn || state === 'loading') {
      return null;
    }

    const handleItemClick = (index: number): void => {
      setOpenedElement(state.items[index]);
      setIsModalOpen(true);
    };
    const handleCloseModal = (): void => {
      setIsModalOpen(false);
      setOpenedElement(null);
    };

    return (
      <div>
        {state.items.length !== 0 && (
          <>
            {galleryTitle && (
              <FlexColumn alignItems="center">
                <Spacer height="120px" />
                <H1>{galleryTitle}</H1>
                <Spacer height="48px" />
              </FlexColumn>
            )}
            <Spacer height="24px" />
            <Gallery
              ref={ref}
              items={state.items}
              onItemClick={handleItemClick}
            />
            {state.pages && (
              <FlexColumn alignItems="center" width="100%">
                <Spacer height="40px" />
                <Pagination
                  current={state.pages.current}
                  total={state.pages.total}
                  onSelect={onSelectPage}
                />
              </FlexColumn>
            )}
          </>
        )}
        <PreviewModal
          isOpen={isModalOpen}
          item={openedElement!}
          onClose={handleCloseModal}
        />
      </div>
    );
  }
);

GallerySection.displayName = 'GallerySection';

export default withEventScope(GallerySection, { feature: 'gallerySection' });
